import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  RefreshCw, Save, Plus, Search, Trash2, FileSpreadsheet, FileText, 
  CheckCircle, X, Edit, DollarSign, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { mondaySettlingApi } from '../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status constants
const STATUS = {
  NEW: 'NEW',
  SETTLED: 'SETTLED'
};

// Generate UUID
const generateId = () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Column definitions for keyboard navigation
const COLUMNS = [
  'tallied', 'clientName', 'displayName', 'group', 
  'clientPL', 'clientAt', 'clientCommPct',
  'parentId', 'parentAt', 'parentCommPct', 'multiplier'
];

export function MondaySettlingPage() {
  // State
  const [activeTab, setActiveTab] = useState('online-client');
  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [matchSettlements, setMatchSettlements] = useState([]); // Match settlements from declared matches
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false); // Add/Edit mode toggle
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Cell references for keyboard navigation
  const cellRefs = useRef({});
  const [focusedCell, setFocusedCell] = useState({ row: 0, col: 0 });
  
  // Settlement screen state
  const [selectedClient, setSelectedClient] = useState(null);
  const [toPayItems, setToPayItems] = useState([]);
  const [toReceiveItems, setToReceiveItems] = useState([]);
  
  // Dialog states
  const [settleAllConfirm, setSettleAllConfirm] = useState(false);
  
  // Account Posting state
  const [postingClient, setPostingClient] = useState('');
  const [postingAmount, setPostingAmount] = useState('');
  const [postingNarration, setPostingNarration] = useState('');
  
  // Settlement CRUD state
  const [settlementDialog, setSettlementDialog] = useState({ open: false, mode: 'add', type: 'pay', item: null });
  const [settlementForm, setSettlementForm] = useState({ displayName: '', amount: '', date: new Date().toISOString().split('T')[0] });

  // Register cell ref
  const setCellRef = (rowIdx, colIdx, ref) => {
    const key = `${rowIdx}-${colIdx}`;
    if (ref) {
      cellRefs.current[key] = ref;
    }
  };

  // Focus cell
  const focusCell = (rowIdx, colIdx) => {
    const key = `${rowIdx}-${colIdx}`;
    const ref = cellRefs.current[key];
    if (ref) {
      ref.focus();
      if (ref.select) ref.select();
      setFocusedCell({ row: rowIdx, col: colIdx });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIdx, colIdx, entry) => {
    const isLastColumn = colIdx === COLUMNS.length - 1;
    const isLastRow = rowIdx === entries.length - 1;
    
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      
      if (isLastColumn) {
        // Last column - move to next row or add new row
        if (isLastRow) {
          // Add new row and focus first editable column
          handleAddRow();
          setTimeout(() => focusCell(rowIdx + 1, 1), 50); // Focus clientName
        } else {
          // Move to next row first column
          focusCell(rowIdx + 1, 1);
        }
      } else {
        // Move to next column
        focusCell(rowIdx, colIdx + 1);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (rowIdx < entries.length - 1) {
        focusCell(rowIdx + 1, colIdx);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIdx > 0) {
        focusCell(rowIdx - 1, colIdx);
      }
    } else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value?.length) {
      if (colIdx < COLUMNS.length - 1) {
        focusCell(rowIdx, colIdx + 1);
      }
    } else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      if (colIdx > 0) {
        focusCell(rowIdx, colIdx - 1);
      }
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch clients from main app
      const clientsRes = await fetch(`${API_URL}/api/clients`, {
        credentials: 'include'
      });
      
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }
      
      // Fetch monday settling entries
      const entriesRes = await fetch(`${API_URL}/api/monday-settling/entries`, {
        credentials: 'include'
      });
      
      if (entriesRes.ok) {
        const entriesData = await entriesRes.json();
        setEntries(entriesData);
      } else if (entriesRes.status === 404) {
        setEntries([]);
      }
      
      // Fetch monday settlements
      const settlementsRes = await fetch(`${API_URL}/api/monday-settling/settlements`, {
        credentials: 'include'
      });
      
      if (settlementsRes.ok) {
        const settlementsData = await settlementsRes.json();
        setSettlements(settlementsData);
      } else if (settlementsRes.status === 404) {
        setSettlements([]);
      }
      
      // Fetch match settlements (from declared matches)
      try {
        const matchSettlementsRes = await fetch(`${API_URL}/api/settlements`, {
          credentials: 'include'
        });
        if (matchSettlementsRes.ok) {
          const matchSettlementsData = await matchSettlementsRes.json();
          setMatchSettlements(matchSettlementsData);
        }
      } catch (e) {
        console.error('Error fetching match settlements:', e);
        setMatchSettlements([]);
      }
      
    } catch (error) {
      console.error('Failed to load data', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculation logic from document
  const calculateEntry = (entry) => {
    const pl = parseFloat(entry.clientPL) || 0;
    const clientAt = parseFloat(entry.clientAt) || 1;
    const parentAt = parseFloat(entry.parentAt) || 1;
    const clientCommPct = parseFloat(entry.clientCommPct) || 0;
    const parentCommPct = parseFloat(entry.parentCommPct) || 0;
    
    // grossClient = P/L × client @
    const grossClient = pl * clientAt;
    // grossParent = P/L × parent @
    const grossParent = pl * parentAt;
    
    // Commission only on loss (negative gross)
    const clientCommAmount = grossClient < 0 ? grossClient * (clientCommPct / 100) : 0;
    const parentCommAmount = grossParent < 0 ? grossParent * (parentCommPct / 100) : 0;
    
    // clientAmount = grossClient - clientCommAmount
    const clientAmount = grossClient - clientCommAmount;
    // parentAmount = grossParent - parentCommAmount
    const parentAmount = grossParent - parentCommAmount;
    
    // nettAmount = -1 × (clientAmount - parentAmount)
    const nettAmount = -1 * (clientAmount - parentAmount);
    
    return {
      ...entry,
      grossClient: Math.round(grossClient * 100) / 100,
      grossParent: Math.round(grossParent * 100) / 100,
      clientCommAmount: Math.round(clientCommAmount * 100) / 100,
      parentCommAmount: Math.round(parentCommAmount * 100) / 100,
      clientAmount: Math.round(clientAmount * 100) / 100,
      parentAmount: Math.round(parentAmount * 100) / 100,
      nettAmount: Math.round(nettAmount * 100) / 100
    };
  };

  // Add new row at bottom
  const handleAddRow = () => {
    const newEntry = {
      id: generateId(),
      clientName: '',
      displayName: '',
      userType: 'User',
      group: '',
      clientPL: 0,
      clientAt: 1.00,
      clientCommPct: 0,
      clientAmount: 0,
      parentId: '',
      parentAt: 1.00,
      parentCommPct: 0,
      parentAmount: 0,
      multiplier: 1,
      nettAmount: 0,
      status: STATUS.NEW,
      tallied: false,
      createdAt: new Date().toISOString()
    };
    
    setEntries(prev => [...prev, newEntry]);
    setHasChanges(true);
  };

  // Update entry
  const handleUpdateEntry = (id, field, value) => {
    setEntries(prevEntries => {
      return prevEntries.map(entry => {
        if (entry.id === id) {
          const updated = { ...entry, [field]: value };
          
          // If client P/L is modified after settlement, reset to NEW
          if (field === 'clientPL' && entry.status === STATUS.SETTLED) {
            updated.status = STATUS.NEW;
          }
          
          // Recalculate if financial field changed
          if (['clientPL', 'clientAt', 'clientCommPct', 'parentAt', 'parentCommPct', 'multiplier'].includes(field)) {
            return calculateEntry(updated);
          }
          
          return updated;
        }
        return entry;
      });
    });
    setHasChanges(true);
  };

  // Delete row
  const handleDeleteRow = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    setHasChanges(true);
  };

  // Check for duplicate display name
  const checkDuplicateDisplayName = (displayName, excludeId) => {
    return entries.some(
      e => e.displayName?.toLowerCase() === displayName?.toLowerCase() && e.id !== excludeId
    );
  };

  // Settle single entry - posts to both client and parent accounts with P&L double-entry
  const handleSettleEntry = async (entry) => {
    try {
      // Find P&L account
      const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
      if (!plAccount) {
        toast.error('P&L account not found. Please contact support.');
        return;
      }

      // Update entry status
      const updatedEntry = {
        ...entry,
        status: STATUS.SETTLED,
        clientPL: 0,
        clientAmount: 0,
        parentAmount: 0,
        nettAmount: 0,
        settledAt: new Date().toISOString()
      };
      
      // Create settlement records with DOUBLE-ENTRY system
      // Every entry to a client/parent account has a corresponding opposite entry to P&L
      const settlementRecords = [];
      const timestamp = new Date().toISOString();
      
      // Client settlement record with P&L counterpart
      // Client amount is inverted: negative becomes positive (To Receive), positive becomes negative (To Pay)
      if (entry.clientAmount !== 0) {
        const clientSettlementAmount = -1 * entry.clientAmount;
        
        // Entry for Client account
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'CLIENT',
          clientId: entry.clientName,
          clientName: entry.clientName,
          displayName: entry.displayName,
          amount: clientSettlementAmount,
          settledAt: timestamp
        });
        
        // Opposite entry for P&L account (double-entry)
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${entry.displayName} (${entry.clientName})`,
          amount: -1 * clientSettlementAmount, // Opposite of client entry
          settledAt: timestamp
        });
      }
      
      // Parent settlement record with P&L counterpart
      // Parent amount: negative → To Pay (keep negative), positive → To Receive (keep positive)
      if (entry.parentId && entry.parentAmount !== 0) {
        // Entry for Parent account
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PARENT',
          clientId: entry.parentId,
          clientName: entry.parentId,
          displayName: entry.displayName,
          amount: entry.parentAmount,
          settledAt: timestamp
        });
        
        // Opposite entry for P&L account (double-entry)
        settlementRecords.push({
          id: generateId(),
          entryId: entry.id,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${entry.displayName} (${entry.parentId})`,
          amount: -1 * entry.parentAmount, // Opposite of parent entry
          settledAt: timestamp
        });
      }
      
      // Save to backend
      const response = await fetch(`${API_URL}/api/monday-settling/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entry: updatedEntry,
          settlements: settlementRecords
        })
      });
      
      if (response.ok) {
        // Update local state
        setEntries(prevEntries => 
          prevEntries.map(e => e.id === entry.id ? updatedEntry : e)
        );
        setSettlements(prev => [...prev, ...settlementRecords]);
        
        toast.success(`Settled: ${entry.displayName || entry.clientName}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Settlement failed');
      }
    } catch (error) {
      console.error('Settlement failed', error);
      toast.error('Settlement failed');
    }
  };

  // Settle all entries
  const handleSettleAll = async () => {
    const unsettledEntries = entries.filter(e => e.status === STATUS.NEW && (e.clientAmount !== 0 || e.parentAmount !== 0));
    
    if (unsettledEntries.length === 0) {
      toast.info('No entries to settle');
      return;
    }
    
    try {
      for (const entry of unsettledEntries) {
        await handleSettleEntry(entry);
      }
      toast.success(`Settled ${unsettledEntries.length} entries`);
      setSettleAllConfirm(false);
    } catch (error) {
      toast.error('Failed to settle all entries');
    }
  };

  // Save all entries
  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/monday-settling/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entries })
      });
      
      if (response.ok) {
        toast.success('Data saved successfully');
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save data');
    }
  };

  // Clear all data
  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/monday-settling/clear`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setEntries([]);
      setSettlements([]);
      toast.success('Data cleared');
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    const data = entries.map((e, idx) => ({
      'S.No': idx + 1,
      'Client Name': e.clientName,
      'Display Name': e.displayName,
      'User Type': e.userType,
      'Group': e.group,
      'Client P/L': e.clientPL,
      'Client @': e.clientAt,
      'Client Comm %': e.clientCommPct,
      'Client Amount': e.clientAmount,
      'Parent ID': e.parentId,
      'Parent @': e.parentAt,
      'Parent Comm %': e.parentCommPct,
      'Parent Amount': e.parentAmount,
      'Nett Amount': -1 * e.nettAmount,
      'Status': e.status
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OnlineClient');
    XLSX.writeFile(wb, `monday_settling_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    toast.success('Excel exported');
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.setTextColor(212, 175, 55);
    doc.text('Monday Settling - Online Client', 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    const headers = [['S.No', 'Client', 'Display', 'P/L', 'Client Amt', 'Parent', 'Parent Amt', 'Nett', 'Status']];
    const rows = entries.map((e, idx) => [
      idx + 1,
      e.clientName,
      e.displayName,
      e.clientPL?.toFixed(2),
      e.clientAmount?.toFixed(2),
      e.parentId,
      e.parentAmount?.toFixed(2),
      (-1 * (e.nettAmount || 0)).toFixed(2),
      e.status
    ]);
    
    doc.autoTable({
      head: headers,
      body: rows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] }
    });
    
    doc.save(`monday_settling_${format(new Date(), 'yyyyMMdd')}.pdf`);
    toast.success('PDF exported');
  };

  // Calculate totals
  const totals = entries.reduce((acc, entry) => {
    acc.clientPL += parseFloat(entry.clientPL) || 0;
    acc.clientAmount += parseFloat(entry.clientAmount) || 0;
    acc.parentAmount += parseFloat(entry.parentAmount) || 0;
    acc.nettAmount += parseFloat(entry.nettAmount) || 0;
    return acc;
  }, { clientPL: 0, clientAmount: 0, parentAmount: 0, nettAmount: 0 });

  // Filter entries based on search
  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.clientName?.toLowerCase().includes(search) ||
      entry.displayName?.toLowerCase().includes(search) ||
      entry.group?.toLowerCase().includes(search)
    );
  });

  // Get row style based on status
  const getRowStyle = (entry) => {
    if (selectedEntry?.id === entry.id) return 'bg-orange-100';
    if (entry.tallied) return 'bg-yellow-100';
    if (entry.status === STATUS.SETTLED) return 'bg-gray-200';
    if (entry.status === STATUS.NEW) return 'bg-green-50';
    return '';
  };

  // Settlement screen: Select client and load To Pay / To Receive
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    
    // Filter settlements for this client (both as client and parent)
    const clientSettlements = settlements.filter(s => 
      s.clientName === client.name || s.clientId === client.client_id || s.clientId === client.name
    );
    
    // To Pay: negative amounts (we owe them)
    setToPayItems(clientSettlements.filter(s => s.amount < 0));
    // To Receive: positive amounts (they owe us)
    setToReceiveItems(clientSettlements.filter(s => s.amount > 0));
  };

  // Client balance calculation - combines monday settlements + match settlements
  const getClientBalance = (client) => {
    // Monday settling settlements
    const clientMondaySettlements = settlements.filter(s => 
      s.clientName === client.name || s.clientId === client.client_id || s.clientId === client.name
    );
    const mondayBalance = clientMondaySettlements.reduce((sum, s) => sum + (s.amount || 0), 0);
    
    // Match settlements (from declared matches)
    const clientMatchSettlements = matchSettlements.filter(s => s.client_id === client.client_id);
    const matchBalance = clientMatchSettlements.reduce((sum, s) => sum + (s.net_amount || 0), 0);
    
    return mondayBalance + matchBalance;
  };

  // Account Posting handler with P&L double-entry
  const handleAccountPosting = async () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const amount = parseFloat(postingAmount);
    if (isNaN(amount) || amount === 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!postingClient) {
      toast.error('Please select a target client');
      return;
    }
    
    // Find P&L account for double-entry
    const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
    if (!plAccount) {
      toast.error('P&L account not found. Please contact support.');
      return;
    }
    
    try {
      const newSettlements = [];
      const timestamp = new Date().toISOString();
      const entryId = `posting_${generateId()}`;
      
      // Account Posting Logic with DOUBLE-ENTRY:
      // If amount > 0:
      //   - Selected client: To Receive (positive amount)
      //   - Target client: To Pay (negative amount)
      //   - P&L gets opposite entries for both
      // If amount < 0:
      //   - Selected client: To Pay (negative amount)
      //   - Target client: To Receive (positive amount)
      //   - P&L gets opposite entries for both
      
      if (amount > 0) {
        // Selected client gets To Receive entry (positive amount)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          displayName: postingNarration || `Transfer from ${postingClient}`,
          amount: amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for selected client (negative = To Pay)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${postingNarration || 'Transfer'} (${selectedClient.name})`,
          amount: -amount,
          settledAt: timestamp
        });
        
        // Target client gets To Pay entry (negative amount)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: postingClient,
          clientName: postingClient,
          displayName: postingNarration || `Transfer to ${selectedClient.name}`,
          amount: -amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for target client (positive = To Receive)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${postingNarration || 'Transfer'} (${postingClient})`,
          amount: amount,
          settledAt: timestamp
        });
      } else {
        // amount < 0
        // Selected client gets To Pay entry (negative amount)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          displayName: postingNarration || `Transfer to ${postingClient}`,
          amount: amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for selected client (positive = To Receive)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${postingNarration || 'Transfer'} (${selectedClient.name})`,
          amount: -amount,
          settledAt: timestamp
        });
        
        // Target client gets To Receive entry (positive amount)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'POSTING',
          clientId: postingClient,
          clientName: postingClient,
          displayName: postingNarration || `Transfer from ${selectedClient.name}`,
          amount: -amount,
          settledAt: timestamp
        });
        
        // P&L contra entry for target client (negative = To Pay)
        newSettlements.push({
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${postingNarration || 'Transfer'} (${postingClient})`,
          amount: amount,
          settledAt: timestamp
        });
      }
      
      // Save to backend FIRST
      try {
        await mondaySettlingApi.createSettlements(newSettlements);
      } catch (apiError) {
        // If backend fails, still update local state but show warning
        console.warn('Backend save failed, updating local state only:', apiError);
      }
      
      // Add to settlements state
      const updatedSettlements = [...settlements, ...newSettlements];
      setSettlements(updatedSettlements);
      
      // Refresh the To Pay / To Receive lists for selected client
      const clientSettlements = updatedSettlements.filter(s => 
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));
      
      // Clear form
      setPostingClient('');
      setPostingAmount('');
      setPostingNarration('');
      
      toast.success('Account posting saved successfully');
    } catch (error) {
      console.error('Account posting error:', error);
      toast.error('Failed to save account posting');
    }
  };

  // Settlement CRUD: Open Add Dialog
  const handleOpenAddSettlement = (type) => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    setSettlementForm({ displayName: '', amount: '', date: new Date().toISOString().split('T')[0] });
    setSettlementDialog({ open: true, mode: 'add', type, item: null });
  };

  // Settlement CRUD: Open Edit Dialog
  const handleOpenEditSettlement = (item, type) => {
    setSettlementForm({
      displayName: item.displayName || '',
      amount: Math.abs(item.amount).toString(),
      date: item.settledAt ? item.settledAt.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setSettlementDialog({ open: true, mode: 'edit', type, item });
  };

  // Settlement CRUD: Save (Add/Edit) with P&L double-entry
  const handleSaveSettlement = async () => {
    const { mode, type, item } = settlementDialog;
    const amount = parseFloat(settlementForm.amount);
    
    if (!settlementForm.displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }
    
    // Find P&L account for double-entry
    const plAccount = clients.find(c => c.is_system_account && c.system_account_type === 'PL');
    if (!plAccount) {
      toast.error('P&L account not found. Please contact support.');
      return;
    }

    try {
      const timestamp = new Date(settlementForm.date).toISOString();
      // For To Pay: store as negative, For To Receive: store as positive
      const finalAmount = type === 'pay' ? -Math.abs(amount) : Math.abs(amount);
      const entryId = `manual_${generateId()}`;

      if (mode === 'add') {
        // Add new settlement with P&L double-entry
        const newSettlements = [];
        
        // Client entry
        const clientSettlement = {
          id: generateId(),
          entryId: entryId,
          accountType: 'MANUAL',
          clientId: selectedClient.client_id,
          clientName: selectedClient.name,
          displayName: settlementForm.displayName,
          amount: finalAmount,
          settledAt: timestamp
        };
        newSettlements.push(clientSettlement);
        
        // P&L contra entry (opposite amount)
        const plContraSettlement = {
          id: generateId(),
          entryId: entryId,
          accountType: 'PL_CONTRA',
          clientId: plAccount.client_id,
          clientName: plAccount.name,
          displayName: `${settlementForm.displayName} (${selectedClient.name})`,
          amount: -finalAmount, // Opposite of client entry
          settledAt: timestamp
        };
        newSettlements.push(plContraSettlement);

        const updatedSettlements = [...settlements, ...newSettlements];
        setSettlements(updatedSettlements);

        // Save to backend
        try {
          await mondaySettlingApi.createSettlements(newSettlements);
        } catch (e) {
          console.warn('Backend save failed:', e);
        }

        toast.success('Settlement entry added');
      } else {
        // Edit existing settlement - also update the P&L contra entry
        const updatedSettlements = settlements.map(s => {
          if (s.id === item.id) {
            return {
              ...s,
              displayName: settlementForm.displayName,
              amount: finalAmount,
              settledAt: timestamp
            };
          }
          // Find and update the corresponding P&L entry
          if (s.entryId === item.entryId && s.accountType === 'PL_CONTRA') {
            return {
              ...s,
              displayName: `${settlementForm.displayName} (${selectedClient.name})`,
              amount: -finalAmount,
              settledAt: timestamp
            };
          }
          return s;
        });
        setSettlements(updatedSettlements);

        // Update backend - both main entry AND P&L contra entry
        try {
          // Update main entry
          await mondaySettlingApi.updateSettlement(item.id, {
            displayName: settlementForm.displayName,
            amount: finalAmount,
            settledAt: timestamp
          });
          
          // Also update the corresponding P&L contra entry
          const plContraEntry = settlements.find(s => s.entryId === item.entryId && s.accountType === 'PL_CONTRA');
          if (plContraEntry) {
            await mondaySettlingApi.updateSettlement(plContraEntry.id, {
              displayName: `${settlementForm.displayName} (${selectedClient.name})`,
              amount: -finalAmount, // Opposite of client entry
              settledAt: timestamp
            });
          }
        } catch (e) {
          console.warn('Backend update failed:', e);
        }

        toast.success('Settlement entry updated');
      }

      // Refresh To Pay / To Receive lists
      const clientSettlements = (mode === 'add' ? [...settlements, { id: generateId(), clientName: selectedClient.name, displayName: settlementForm.displayName, amount: finalAmount, settledAt: timestamp }] : settlements.map(s => s.id === item?.id ? { ...s, displayName: settlementForm.displayName, amount: finalAmount, settledAt: timestamp } : s)).filter(s =>
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));

      setSettlementDialog({ open: false, mode: 'add', type: 'pay', item: null });
    } catch (error) {
      console.error('Settlement save error:', error);
      toast.error('Failed to save settlement');
    }
  };

  // Settlement CRUD: Delete with P&L double-entry cleanup
  const handleDeleteSettlement = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.displayName}"?`)) {
      return;
    }

    try {
      // Remove the item and its corresponding P&L contra entry (same entryId)
      const updatedSettlements = settlements.filter(s => 
        s.id !== item.id && !(s.entryId === item.entryId && s.accountType === 'PL_CONTRA')
      );
      setSettlements(updatedSettlements);

      // Delete from backend
      try {
        await mondaySettlingApi.deleteSettlement(item.id);
        // Also delete P&L contra entries with same entryId
        const plContraEntry = settlements.find(s => s.entryId === item.entryId && s.accountType === 'PL_CONTRA');
        if (plContraEntry) {
          await mondaySettlingApi.deleteSettlement(plContraEntry.id);
        }
      } catch (e) {
        console.warn('Backend delete failed:', e);
      }

      // Refresh To Pay / To Receive lists
      const clientSettlements = updatedSettlements.filter(s =>
        s.clientName === selectedClient.name || s.clientId === selectedClient.client_id || s.clientId === selectedClient.name
      );
      setToPayItems(clientSettlements.filter(s => s.amount < 0));
      setToReceiveItems(clientSettlements.filter(s => s.amount > 0));

      toast.success('Settlement entry deleted');
    } catch (error) {
      console.error('Settlement delete error:', error);
      toast.error('Failed to delete settlement');
    }
  };

  // Settlement: Print
  const handlePrintSettlement = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    const balance = getClientBalance(selectedClient);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Settlement - ${selectedClient.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #DAA520; padding-bottom: 10px; }
          .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .balance { font-size: 18px; font-weight: bold; color: ${balance >= 0 ? '#2563eb' : '#dc2626'}; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .amount { text-align: right; }
          .pay { color: #dc2626; }
          .receive { color: #2563eb; }
          .total-row { font-weight: bold; background-color: #f9f9f9; }
          .section { margin-bottom: 30px; }
          .print-date { color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>YellowGold Pro - Settlement Statement</h1>
        <div class="header">
          <div>
            <strong>Client:</strong> ${selectedClient.name}<br/>
            <strong>Current Balance:</strong> <span class="balance">${balance.toFixed(2)}</span>
          </div>
          <div class="print-date">
            Printed on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
        
        <div class="section">
          <h3 style="color: #dc2626;">To Pay (${toPayItems.length})</h3>
          <table>
            <thead><tr><th>Date</th><th>Display Name</th><th class="amount">Amount</th></tr></thead>
            <tbody>
              ${toPayItems.map(item => `
                <tr>
                  <td>${format(new Date(item.settledAt), 'dd/MM/yyyy')}</td>
                  <td>${item.displayName}</td>
                  <td class="amount pay">${Math.abs(item.amount).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2" style="text-align: right;">Total:</td>
                <td class="amount pay">${toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <h3 style="color: #2563eb;">To Receive (${toReceiveItems.length})</h3>
          <table>
            <thead><tr><th>Date</th><th>Display Name</th><th class="amount">Amount</th></tr></thead>
            <tbody>
              ${toReceiveItems.map(item => `
                <tr>
                  <td>${format(new Date(item.settledAt), 'dd/MM/yyyy')}</td>
                  <td>${item.displayName}</td>
                  <td class="amount receive">${Math.abs(item.amount).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2" style="text-align: right;">Total:</td>
                <td class="amount receive">${toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Settlement: Export PDF for selected client
  const handleExportSettlementPDF = () => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const doc = new jsPDF();
    const balance = getClientBalance(selectedClient);
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(218, 165, 32); // Gold color
    doc.text('YellowGold Pro - Settlement Statement', 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Client: ${selectedClient.name}`, 14, 35);
    doc.text(`Current Balance: ${balance.toFixed(2)}`, 14, 42);
    doc.text(`Date: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 49);
    
    let yPos = 60;
    
    // To Pay section
    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38); // Red
    doc.text(`To Pay (${toPayItems.length})`, 14, yPos);
    yPos += 5;
    
    if (toPayItems.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Display Name', 'Amount']],
        body: toPayItems.map(item => [
          format(new Date(item.settledAt), 'dd/MM/yyyy'),
          item.displayName,
          Math.abs(item.amount).toFixed(2)
        ]),
        foot: [['', 'Total:', toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)]],
        headStyles: { fillColor: [220, 38, 38] },
        footStyles: { fillColor: [245, 245, 245], textColor: [220, 38, 38], fontStyle: 'bold' }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    } else {
      yPos += 10;
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text('No items to pay', 14, yPos);
      yPos += 15;
    }
    
    // To Receive section
    doc.setFontSize(14);
    doc.setTextColor(37, 99, 235); // Blue
    doc.text(`To Receive (${toReceiveItems.length})`, 14, yPos);
    yPos += 5;
    
    if (toReceiveItems.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Date', 'Display Name', 'Amount']],
        body: toReceiveItems.map(item => [
          format(new Date(item.settledAt), 'dd/MM/yyyy'),
          item.displayName,
          Math.abs(item.amount).toFixed(2)
        ]),
        foot: [['', 'Total:', toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)]],
        headStyles: { fillColor: [37, 99, 235] },
        footStyles: { fillColor: [245, 245, 245], textColor: [37, 99, 235], fontStyle: 'bold' }
      });
    }
    
    doc.save(`settlement_${selectedClient.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
    toast.success('Settlement PDF exported');
  };

  // Settlement: Exit (go back to OnlineClient tab)
  const handleExitSettlement = () => {
    setSelectedClient(null);
    setToPayItems([]);
    setToReceiveItems([]);
    setActiveTab('online-client');
  };

  // Editable cell component for spreadsheet-style input
  const EditableCell = ({ value, onChange, onKeyDown, type = 'text', step, placeholder, rowIdx, colIdx, disabled }) => {
    return (
      <input
        ref={(ref) => setCellRef(rowIdx, colIdx, ref)}
        type={type}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
        onKeyDown={(e) => onKeyDown(e, rowIdx, colIdx)}
        onFocus={(e) => {
          e.target.select();
          setFocusedCell({ row: rowIdx, col: colIdx });
        }}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
        style={{ minWidth: type === 'number' ? '60px' : '80px' }}
      />
    );
  };

  return (
    <div className="h-full flex flex-col" data-testid="monday-settling-page">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-2 mb-2">
          <TabsTrigger value="online-client" data-testid="tab-online-client">
            <Users className="h-4 w-4 mr-1" />
            OnlineClient
          </TabsTrigger>
          <TabsTrigger value="settlement" data-testid="tab-settlement">
            <DollarSign className="h-4 w-4 mr-1" />
            Settlement
          </TabsTrigger>
        </TabsList>

        {/* OnlineClient Tab */}
        <TabsContent value="online-client" className="flex-1 flex flex-col space-y-2">
          {/* Toolbar */}
          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded border" data-testid="toolbar">
            <div className="flex items-center gap-1">
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 w-40 text-xs"
                data-testid="search-input"
              />
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSearchTerm('')}>
                <X className="h-3 w-3" />
              </Button>
            </div>
            
            <div className="h-4 w-px bg-border" />
            
            {/* Add/Edit Mode Toggle Button */}
            <Button 
              variant={editMode ? "default" : "outline"} 
              size="sm" 
              className={`h-7 text-xs ${editMode ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
              onClick={() => setEditMode(!editMode)}
              data-testid="edit-mode-btn"
            >
              <Edit className="h-3 w-3 mr-1" />
              {editMode ? 'Done Editing' : 'Add/Edit'}
            </Button>
            
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleAddRow} data-testid="add-row-btn">
              <Plus className="h-3 w-3 mr-1" />
              Add Row
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs"
              onClick={handleSave}
              disabled={!hasChanges}
              data-testid="save-btn"
            >
              <Save className="h-3 w-3 mr-1" />
              Save
            </Button>
            
            <div className="h-4 w-px bg-border" />
            
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleClearData}>
              <Trash2 className="h-3 w-3 mr-1" />
              Clear
            </Button>
            
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-3 w-3 mr-1" />
              Excel
            </Button>
            
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExportPDF}>
              <FileText className="h-3 w-3 mr-1" />
              PDF
            </Button>
            
            <div className="h-4 w-px bg-border" />
            
            <Button 
              variant="default" 
              size="sm" 
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setSettleAllConfirm(true)}
              data-testid="settle-all-btn"
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              Settle All A/c
            </Button>
            
            <div className="ml-auto flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Tab/Enter: Next cell</span>
              <span className="text-muted-foreground">|</span>
              <span className="text-emerald-600 font-medium">
                {entries.filter(e => e.status === STATUS.NEW).length} New
              </span>
              <span>/</span>
              <span className="text-gray-500 font-medium">
                {entries.filter(e => e.status === STATUS.SETTLED).length} Settled
              </span>
            </div>
          </div>

          {/* Spreadsheet Grid */}
          <div className="flex-1 overflow-auto border rounded">
            <table className="w-full text-xs border-collapse" style={{ fontFamily: 'Consolas, monospace' }}>
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th colSpan={5} className="border-r bg-muted/80 px-1 py-0.5 text-center text-[10px]">Client Info</th>
                  <th colSpan={4} className="border-r bg-blue-100 px-1 py-0.5 text-center text-[10px]">User Info</th>
                  <th colSpan={4} className="border-r bg-amber-100 px-1 py-0.5 text-center text-[10px]">Parent Info</th>
                  <th colSpan={3} className="bg-muted/80 px-1 py-0.5 text-center text-[10px]">Result</th>
                </tr>
                <tr className="bg-muted border-b">
                  <th className="px-1 py-1 text-center w-8 border-r">✓</th>
                  <th className="px-1 py-1 text-left min-w-[120px]">Client Name</th>
                  <th className="px-1 py-1 text-left min-w-[100px]">Display Name</th>
                  <th className="px-1 py-1 text-left min-w-[100px] border-r">Group</th>
                  <th className="px-1 py-1 text-right w-24">P/L</th>
                  <th className="px-1 py-1 text-right w-16">@</th>
                  <th className="px-1 py-1 text-right w-16">Comm%</th>
                  <th className="px-1 py-1 text-right w-24 border-r">Amount</th>
                  <th className="px-1 py-1 text-left min-w-[100px]">Parent ID</th>
                  <th className="px-1 py-1 text-right w-16">@</th>
                  <th className="px-1 py-1 text-right w-16">Comm%</th>
                  <th className="px-1 py-1 text-right w-24 border-r">Amount</th>
                  <th className="px-1 py-1 text-right w-12">×</th>
                  <th className="px-1 py-1 text-right w-24">Nett</th>
                  <th className="px-1 py-1 text-center w-16">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry, rowIdx) => (
                  <tr 
                    key={entry.id} 
                    className={`border-b hover:bg-blue-50/50 ${getRowStyle(entry)}`}
                    onClick={() => setSelectedEntry(entry)}
                  >
                    {/* Tallied Checkbox */}
                    <td className="px-1 py-0.5 text-center border-r">
                      <Checkbox
                        checked={entry.tallied}
                        onCheckedChange={(checked) => handleUpdateEntry(entry.id, 'tallied', checked)}
                        ref={(ref) => setCellRef(rowIdx, 0, ref)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 0, entry)}
                      />
                    </td>
                    
                    {/* Client Name - Select */}
                    <td className="px-0 py-0">
                      <select
                        ref={(ref) => setCellRef(rowIdx, 1, ref)}
                        value={entry.clientName}
                        onChange={(e) => handleUpdateEntry(entry.id, 'clientName', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 1, entry)}
                        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                      >
                        <option value="">-- Select --</option>
                        {clients.map(c => (
                          <option key={c.client_id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    
                    {/* Display Name - Direct input */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 2, ref)}
                        type="text"
                        value={entry.displayName ?? ''}
                        onChange={(e) => handleUpdateEntry(entry.id, 'displayName', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 2, entry)}
                        onFocus={(e) => {
                          e.target.select();
                          setFocusedCell({ row: rowIdx, col: 2 });
                        }}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        placeholder="Enter name"
                        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '100px' }}
                      />
                    </td>
                    
                    {/* Group - Select */}
                    <td className="px-0 py-0 border-r">
                      <select
                        ref={(ref) => setCellRef(rowIdx, 3, ref)}
                        value={entry.group}
                        onChange={(e) => handleUpdateEntry(entry.id, 'group', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 3, entry)}
                        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                      >
                        <option value="">-- Select --</option>
                        {clients.filter(c => c.name !== entry.clientName).map(c => (
                          <option key={c.client_id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    
                    {/* P/L - Allow negative via keyboard */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 4, ref)}
                        type="text"
                        inputMode="decimal"
                        value={entry.clientPL ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow empty, minus sign, or valid number
                          if (val === '' || val === '-' || !isNaN(parseFloat(val))) {
                            handleUpdateEntry(entry.id, 'clientPL', val === '' || val === '-' ? val : parseFloat(val));
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          handleUpdateEntry(entry.id, 'clientPL', val);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 4, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className={`w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none ${
                          entry.clientPL >= 0 ? 'text-blue-600' : 'text-red-600'
                        }`}
                        style={{ minWidth: '70px' }}
                      />
                    </td>
                    
                    {/* Client @ - Decimal 2 digits */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 5, ref)}
                        type="text"
                        inputMode="decimal"
                        value={typeof entry.clientAt === 'number' ? entry.clientAt.toFixed(2) : (entry.clientAt ?? '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === '.' || !isNaN(parseFloat(val)) || /^\d*\.?\d*$/.test(val)) {
                            handleUpdateEntry(entry.id, 'clientAt', val);
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 1;
                          handleUpdateEntry(entry.id, 'clientAt', Math.round(val * 100) / 100);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 5, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className="w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '50px' }}
                      />
                    </td>
                    
                    {/* Client Comm% */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 6, ref)}
                        type="text"
                        inputMode="decimal"
                        value={entry.clientCommPct ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            handleUpdateEntry(entry.id, 'clientCommPct', val === '' ? '' : parseFloat(val));
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          handleUpdateEntry(entry.id, 'clientCommPct', val);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 6, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className="w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '50px' }}
                      />
                    </td>
                    
                    {/* Client Amount - Calculated */}
                    <td className="px-1 py-0.5 text-right border-r font-medium">
                      <span className={entry.clientAmount >= 0 ? 'text-blue-600' : 'text-red-600'}>
                        {(entry.clientAmount || 0).toFixed(2)}
                      </span>
                    </td>
                    
                    {/* Parent ID - Select */}
                    <td className="px-0 py-0">
                      <select
                        ref={(ref) => setCellRef(rowIdx, 7, ref)}
                        value={entry.parentId}
                        onChange={(e) => handleUpdateEntry(entry.id, 'parentId', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 7, entry)}
                        className="w-full h-6 px-1 text-xs border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                      >
                        <option value="">-- Select --</option>
                        {clients.filter(c => c.name !== entry.clientName).map(c => (
                          <option key={c.client_id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    
                    {/* Parent @ - Decimal 2 digits */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 8, ref)}
                        type="text"
                        inputMode="decimal"
                        value={typeof entry.parentAt === 'number' ? entry.parentAt.toFixed(2) : (entry.parentAt ?? '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === '.' || !isNaN(parseFloat(val)) || /^\d*\.?\d*$/.test(val)) {
                            handleUpdateEntry(entry.id, 'parentAt', val);
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 1;
                          handleUpdateEntry(entry.id, 'parentAt', Math.round(val * 100) / 100);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 8, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className="w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '50px' }}
                      />
                    </td>
                    
                    {/* Parent Comm% */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 9, ref)}
                        type="text"
                        inputMode="decimal"
                        value={entry.parentCommPct ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            handleUpdateEntry(entry.id, 'parentCommPct', val === '' ? '' : parseFloat(val));
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          handleUpdateEntry(entry.id, 'parentCommPct', val);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 9, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className="w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '50px' }}
                      />
                    </td>
                    
                    {/* Parent Amount - Calculated */}
                    <td className="px-1 py-0.5 text-right border-r font-medium">
                      <span className={entry.parentAmount >= 0 ? 'text-blue-600' : 'text-red-600'}>
                        {(entry.parentAmount || 0).toFixed(2)}
                      </span>
                    </td>
                    
                    {/* Multiplier */}
                    <td className="px-0 py-0">
                      <input
                        ref={(ref) => setCellRef(rowIdx, 10, ref)}
                        type="text"
                        inputMode="decimal"
                        value={entry.multiplier ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || !isNaN(parseFloat(val))) {
                            handleUpdateEntry(entry.id, 'multiplier', val === '' ? '' : parseFloat(val));
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value) || 1;
                          handleUpdateEntry(entry.id, 'multiplier', val);
                        }}
                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 10, entry)}
                        onFocus={(e) => e.target.select()}
                        disabled={!editMode && entry.status === STATUS.SETTLED}
                        className="w-full h-6 px-1 text-xs text-right border-0 bg-transparent focus:bg-white focus:ring-1 focus:ring-primary outline-none"
                        style={{ minWidth: '40px' }}
                      />
                    </td>
                    
                    {/* Nett Amount - Calculated */}
                    <td className="px-1 py-0.5 text-right font-bold">
                      <span className={(-1 * (entry.nettAmount || 0)) >= 0 ? 'text-blue-600' : 'text-red-600'}>
                        {(-1 * (entry.nettAmount || 0)).toFixed(2)}
                      </span>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-1 py-0.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-5 text-[10px] px-1"
                          disabled={entry.status === STATUS.SETTLED}
                          onClick={(e) => { e.stopPropagation(); handleSettleEntry(entry); }}
                          data-testid={`settle-${entry.id}`}
                        >
                          ✓
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); handleDeleteRow(entry.id); }}
                        >
                          ×
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Empty row hint */}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={15} className="px-4 py-8 text-center text-muted-foreground">
                      No entries. Click "Add Row" or press Tab in the last cell to add a new row.
                    </td>
                  </tr>
                )}
              </tbody>
              
              {/* Totals Row */}
              <tfoot className="bg-muted font-bold sticky bottom-0">
                <tr style={{ height: '28px' }}>
                  <td colSpan={4} className="px-1 py-1 text-right border-r">Totals:</td>
                  <td className="px-1 py-1 text-right">
                    <span className={totals.clientPL >= 0 ? 'text-blue-600' : 'text-red-600'}>
                      {totals.clientPL.toFixed(2)}
                    </span>
                  </td>
                  <td colSpan={2}></td>
                  <td className="px-1 py-1 text-right border-r">
                    <span className={totals.clientAmount >= 0 ? 'text-blue-600' : 'text-red-600'}>
                      {totals.clientAmount.toFixed(2)}
                    </span>
                  </td>
                  <td colSpan={3}></td>
                  <td className="px-1 py-1 text-right border-r">
                    <span className={totals.parentAmount >= 0 ? 'text-blue-600' : 'text-red-600'}>
                      {totals.parentAmount.toFixed(2)}
                    </span>
                  </td>
                  <td></td>
                  <td className="px-1 py-1 text-right">
                    <span className={(-1 * totals.nettAmount) >= 0 ? 'text-blue-600' : 'text-red-600'}>
                      {(-1 * totals.nettAmount).toFixed(2)}
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>

        {/* Settlement Tab */}
        <TabsContent value="settlement" className="flex-1 p-0 m-0 data-[state=active]:flex data-[state=active]:flex-col">
          <div className="grid grid-cols-12 gap-2 items-start">
            {/* All Clients Grid (Left) */}
            <div className="col-span-3 flex flex-col border rounded h-[calc(100vh-180px)]">
              <div className="bg-muted px-2 py-1 text-xs font-medium border-b shrink-0">All Clients</div>
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-1 py-1 text-left">Client Name</th>
                      <th className="px-1 py-1 text-right">Current Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map(client => {
                      const balance = getClientBalance(client);
                      return (
                        <tr 
                          key={client.client_id}
                          className={`border-b cursor-pointer hover:bg-muted/30 ${
                            selectedClient?.client_id === client.client_id ? 'bg-orange-100' : ''
                          }`}
                          onClick={() => handleSelectClient(client)}
                          style={{ height: '24px' }}
                        >
                          <td className="px-1 py-0.5 truncate max-w-[100px]" title={client.name}>
                            {client.name}
                          </td>
                          <td className={`px-1 py-0.5 text-right ${balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {balance.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Center: Client Details + To Pay / To Receive */}
            <div className="col-span-9 flex flex-col gap-2">
              {/* Selected Client Info - At Top */}
              {selectedClient ? (
                <div className="bg-muted/30 rounded p-2 border">
                  <div className="text-center font-medium text-lg">{selectedClient.name}</div>
                  <div className="flex justify-center gap-8 mt-1 text-xs">
                    <div>
                      <span className="text-muted-foreground">Old Balance: </span>
                      <span className="font-medium">0.00</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Current Balance: </span>
                      <span className={`font-medium ${getClientBalance(selectedClient) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {getClientBalance(selectedClient).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Transactions: </span>
                      <span className="font-medium">{toPayItems.length + toReceiveItems.length}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-muted/30 rounded p-4 border text-center text-muted-foreground">
                  Select a client from the list to view settlement details
                </div>
              )}

              {/* To Pay / To Receive Grids */}
              <div className="grid grid-cols-2 gap-2">
                {/* To Pay */}
                <div className="flex flex-col border rounded h-[200px]">
                  <div className="bg-red-50 px-2 py-1 text-xs font-medium border-b text-red-700 shrink-0">
                    To Pay ({toPayItems.length})
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1 text-left">Date</th>
                          <th className="px-1 py-1 text-left">Display Name</th>
                          <th className="px-1 py-1 text-right">Amount</th>
                          <th className="px-1 py-1 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {toPayItems.map(item => (
                          <tr key={item.id} className="border-b hover:bg-red-50/50" style={{ height: '24px' }}>
                            <td className="px-1 py-0.5">{format(new Date(item.settledAt), 'dd/MM')}</td>
                            <td className="px-1 py-0.5">{item.displayName}</td>
                            <td className="px-1 py-0.5 text-right text-red-600">
                              {Math.abs(item.amount).toFixed(2)}
                            </td>
                            <td className="px-1 py-0.5">
                              <div className="flex gap-0.5">
                                <button 
                                  className="text-blue-600 hover:text-blue-800 text-xs" 
                                  onClick={() => handleOpenEditSettlement(item, 'pay')}
                                  title="Edit"
                                >✎</button>
                                <button 
                                  className="text-red-600 hover:text-red-800 text-xs" 
                                  onClick={() => handleDeleteSettlement(item)}
                                  title="Delete"
                                >✕</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {toPayItems.length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                              No items to pay
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50 font-medium sticky bottom-0">
                        <tr>
                          <td colSpan={2} className="px-1 py-1 text-right">Total:</td>
                          <td className="px-1 py-1 text-right text-red-600">
                            {toPayItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {/* CRUD Controls */}
                  <div className="flex gap-1 p-1 border-t bg-muted/30 shrink-0">
                    <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => handleOpenAddSettlement('pay')}>Add</Button>
                  </div>
                </div>

                {/* To Receive */}
                <div className="flex flex-col border rounded h-[200px]">
                  <div className="bg-blue-50 px-2 py-1 text-xs font-medium border-b text-blue-700 shrink-0">
                    To Receive ({toReceiveItems.length})
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1 text-left">Date</th>
                          <th className="px-1 py-1 text-left">Display Name</th>
                          <th className="px-1 py-1 text-right">Amount</th>
                          <th className="px-1 py-1 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {toReceiveItems.map(item => (
                          <tr key={item.id} className="border-b hover:bg-blue-50/50" style={{ height: '24px' }}>
                            <td className="px-1 py-0.5">{format(new Date(item.settledAt), 'dd/MM')}</td>
                            <td className="px-1 py-0.5">{item.displayName}</td>
                            <td className="px-1 py-0.5 text-right text-blue-600">
                              {Math.abs(item.amount).toFixed(2)}
                            </td>
                            <td className="px-1 py-0.5">
                              <div className="flex gap-0.5">
                                <button 
                                  className="text-blue-600 hover:text-blue-800 text-xs" 
                                  onClick={() => handleOpenEditSettlement(item, 'receive')}
                                  title="Edit"
                                >✎</button>
                                <button 
                                  className="text-red-600 hover:text-red-800 text-xs" 
                                  onClick={() => handleDeleteSettlement(item)}
                                  title="Delete"
                                >✕</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {toReceiveItems.length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">
                              No items to receive
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-muted/50 font-medium sticky bottom-0">
                        <tr>
                          <td colSpan={2} className="px-1 py-1 text-right">Total:</td>
                          <td className="px-1 py-1 text-right text-blue-600">
                            {toReceiveItems.reduce((sum, i) => sum + Math.abs(i.amount), 0).toFixed(2)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {/* CRUD Controls */}
                  <div className="flex gap-1 p-1 border-t bg-muted/30 shrink-0">
                    <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => handleOpenAddSettlement('receive')}>Add</Button>
                  </div>
                </div>
              </div>

              {/* Account Posting Section (Bottom) */}
              <div className="border rounded p-2 bg-muted/20">
                <div className="text-xs font-medium mb-2 text-muted-foreground">Account Posting</div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Balance</Label>
                    <Input 
                      type="text" 
                      className="h-7 text-xs" 
                      placeholder="0.00"
                      value={selectedClient ? getClientBalance(selectedClient).toFixed(2) : '0.00'}
                      readOnly
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Client</Label>
                    <Select value={postingClient} onValueChange={setPostingClient}>
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.filter(c => c.name !== selectedClient?.name).map(c => (
                          <SelectItem key={c.client_id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Amount</Label>
                    <Input 
                      type="number" 
                      className="h-7 text-xs" 
                      placeholder="0.00"
                      value={postingAmount}
                      onChange={(e) => setPostingAmount(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Narration</Label>
                    <Input 
                      type="text" 
                      className="h-7 text-xs" 
                      placeholder="Enter narration"
                      value={postingNarration}
                      onChange={(e) => setPostingNarration(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-2 justify-end">
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="h-7 text-xs"
                    onClick={handleAccountPosting}
                    disabled={!selectedClient || !postingClient || !postingAmount}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handlePrintSettlement}>
                    <FileText className="h-3 w-3 mr-1" />
                    Print
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExportExcel}>
                    <FileSpreadsheet className="h-3 w-3 mr-1" />
                    Export Excel
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExportSettlementPDF}>
                    <FileText className="h-3 w-3 mr-1" />
                    Export PDF
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleExitSettlement}>
                    Exit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Settlement Add/Edit Dialog */}
      <Dialog open={settlementDialog.open} onOpenChange={(open) => setSettlementDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {settlementDialog.mode === 'add' ? 'Add' : 'Edit'} {settlementDialog.type === 'pay' ? 'To Pay' : 'To Receive'} Entry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Display Name</Label>
              <Input
                value={settlementForm.displayName}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="Enter display name"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Amount</Label>
              <Input
                type="number"
                value={settlementForm.amount}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="mt-1"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <Label className="text-sm">Date</Label>
              <Input
                type="date"
                value={settlementForm.date}
                onChange={(e) => setSettlementForm(prev => ({ ...prev, date: e.target.value }))}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettlementDialog({ open: false, mode: 'add', type: 'pay', item: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettlement}>
              {settlementDialog.mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle All Confirmation Dialog */}
      <Dialog open={settleAllConfirm} onOpenChange={setSettleAllConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle All Accounts</DialogTitle>
            <DialogDescription>
              This will settle all {entries.filter(e => e.status === STATUS.NEW).length} unsettled entries. 
              Amounts will be posted to respective client and parent accounts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleAllConfirm(false)}>Cancel</Button>
            <Button onClick={handleSettleAll} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="h-4 w-4 mr-1" />
              Confirm Settle All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
